/**
 * Connect a price source (C5) — pushed OVER the tabs (title "Connect", with back).
 *
 * Step 1: browse the GLOBAL connector directory (GET /api/price-connectors) with a
 *         search Field + kind filter chips. Tap a connector to select it.
 * Step 2: create a supplier_connection (POST /api/supplier-connections), then show a
 *         "Link" form: optional account number + ONE secret field appropriate to the
 *         connector's link_method (api_key / punchout_shared_secret / edi_partner_id).
 *         POST /api/supplier-connections/:id/link. For retail connectors, show the
 *         ToS / opt-in note. After linking, router.back().
 *
 * Connector directory returns raw snake_case rows. NEVER imports @smartbox/*. No raw styles.
 */

import { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listConnectors,
  createConnection,
  linkConnection,
  type ConnectorRow,
  type LinkConnectionRequest,
} from '../../../src/api/pricing';
import {
  Screen,
  Text,
  Card,
  Button,
  Field,
  Select,
  Badge,
  ListRow,
} from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

const KIND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'distributor', label: 'Distributor' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'retail', label: 'Retail' },
  { value: 'aggregator', label: 'Aggregator' },
];

/** Per-link-method secret field metadata. */
function secretMeta(linkMethod: string): { label: string; placeholder: string; key: keyof NonNullable<LinkConnectionRequest['secrets']> } | null {
  switch (linkMethod) {
    case 'api_key':
      return { label: 'API key', placeholder: 'Paste the API key', key: 'api_key' };
    case 'punchout_shared_secret':
      return { label: 'PunchOut shared secret', placeholder: 'Paste the shared secret', key: 'punchout_shared_secret' };
    case 'edi_partner_id':
      return { label: 'EDI partner ID', placeholder: 'Enter the EDI partner ID', key: 'edi_partner_id' };
    default:
      return null;
  }
}

export default function ConnectPriceSourceScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<string | null>(null);
  const [selected, setSelected] = useState<ConnectorRow | null>(null);

  // Link form state (step 2)
  const [accountNumber, setAccountNumber] = useState('');
  const [secret, setSecret] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const connectorsQuery = useQuery({
    queryKey: ['price-connectors', kind ?? 'all'],
    queryFn: () => listConnectors(kind ? { kind } : {}),
    staleTime: 60_000,
  });

  // Client-side search over already-loaded rows (mirrors the estimates list pattern).
  const connectors = useMemo(() => {
    const rows = connectorsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) =>
      [c.display_name, c.code, c.kind, ...(c.trades_supported ?? [])]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q)),
    );
  }, [connectorsQuery.data, search]);

  const linkMutation = useMutation({
    mutationFn: async (connector: ConnectorRow) => {
      // Step 2a: create the connection from the directory connector.
      const connection = await createConnection({
        connector_id: connector.id,
        connector_code: connector.code,
        account_number: accountNumber.trim() || undefined,
      });
      // Step 2b: link it with the appropriate secret.
      const meta = secretMeta(connector.link_method);
      const body: LinkConnectionRequest = {};
      if (accountNumber.trim()) body.accountNumber = accountNumber.trim();
      if (meta && secret.trim()) {
        body.secrets = { [meta.key]: secret.trim() };
      }
      return linkConnection(connection.id, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['supplier-connections'] });
      router.back();
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not link the connector.')),
  });

  // ---------------------------------------------------------------------------
  // Step 2 — Link form for the selected connector.
  // ---------------------------------------------------------------------------
  if (selected) {
    const meta = secretMeta(selected.link_method);
    const isRetail = selected.kind === 'retail';
    return (
      <Screen scroll>
        <Text variant="title">{selected.display_name}</Text>
        <View style={styles.metaRow}>
          <Badge status="draft" label={humanize(selected.kind)} />
          <Text variant="small" color="secondary">
            {humanize(selected.link_method)}
          </Text>
        </View>

        <Field
          label="Account number (optional)"
          placeholder="Your supplier account number"
          value={accountNumber}
          onChangeText={setAccountNumber}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Account number"
        />

        {meta ? (
          <Field
            label={meta.label}
            placeholder={meta.placeholder}
            value={secret}
            onChangeText={setSecret}
            autoCapitalize="none"
            autoCorrect={false}
            secureTextEntry
            accessibilityLabel={meta.label}
          />
        ) : (
          <Card>
            <Text variant="body" color="secondary">
              This connector links without a credential. Continue to finish setup.
            </Text>
          </Card>
        )}

        {isRetail ? (
          <Card>
            <Text variant="small" color="secondary">
              By connecting a retail source you confirm you have opted in and agree to the
              retailer's Terms of Service for pricing access. Retail prices are for
              reference only.
            </Text>
          </Card>
        ) : null}

        {formError ? (
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {formError}
            </Text>
          </Card>
        ) : null}

        <View style={styles.actions}>
          <Button
            title="Link connector"
            loading={linkMutation.isPending}
            disabled={linkMutation.isPending}
            onPress={() => {
              setFormError(null);
              linkMutation.mutate(selected);
            }}
            accessibilityLabel="Link connector"
          />
          <Button
            title="Back to directory"
            variant="secondary"
            disabled={linkMutation.isPending}
            onPress={() => {
              setSelected(null);
              setSecret('');
              setAccountNumber('');
              setFormError(null);
            }}
          />
        </View>
      </Screen>
    );
  }

  // ---------------------------------------------------------------------------
  // Step 1 — Connector directory.
  // ---------------------------------------------------------------------------
  return (
    <Screen scroll>
      <Text variant="title">Connect a price source</Text>

      <Field
        placeholder="Search connectors"
        value={search}
        onChangeText={setSearch}
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="search"
        clearButtonMode="while-editing"
        accessibilityLabel="Search connectors"
      />

      <Select
        label="Kind"
        options={KIND_OPTIONS}
        value={kind}
        onChange={(v) => setKind(kind === v ? null : v)}
      />

      {connectorsQuery.isLoading ? (
        <View style={styles.inlineState}>
          <ActivityIndicator
            color={theme.color.action.primary}
            accessibilityLabel="Loading connectors"
          />
        </View>
      ) : connectorsQuery.isError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(connectorsQuery.error, 'Failed to load connectors.')}
          </Text>
          <Button
            title="Retry"
            variant="secondary"
            size="sm"
            fullWidth={false}
            onPress={() => connectorsQuery.refetch()}
          />
        </Card>
      ) : connectors.length === 0 ? (
        <Card>
          <Text variant="body" color="secondary">
            {search.trim() || kind
              ? 'No connectors match your filters.'
              : 'No connectors available.'}
          </Text>
        </Card>
      ) : (
        <View style={styles.list}>
          {connectors.map((c: ConnectorRow) => {
            const trades = (c.trades_supported ?? []).join(', ');
            const subtitle = [humanize(c.kind), trades].filter(Boolean).join(' · ');
            return (
              <ListRow
                key={c.id}
                title={c.display_name}
                subtitle={subtitle || c.code}
                right={
                  <View style={styles.rowRight}>
                    <Text variant="small" color="secondary">
                      Tier {c.max_capability_tier}
                    </Text>
                    <Button
                      title="Details"
                      variant="secondary"
                      size="sm"
                      fullWidth={false}
                      onPress={() => router.push(`/(app)/supplier-pricing/connector/${c.id}`)}
                      accessibilityLabel={`View ${c.display_name} details`}
                    />
                  </View>
                }
                onPress={() => {
                  setSelected(c);
                  setFormError(null);
                }}
              />
            );
          })}
        </View>
      )}
    </Screen>
  );
}

const styles = StyleSheet.create({
  list: { gap: theme.spacing.sm },
  inlineState: { paddingVertical: theme.spacing.lg, alignItems: 'center' },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  actions: { gap: theme.spacing.sm },
  rowRight: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
});
